import { Form } from '../../../services/formService';
import { Task } from '../../../types/Task';
import {
  sumPersistedAllocationHoursForWorkCompleted,
  type FormLabourOvertimeAllocationRow,
} from '../../../utils/labourOvertimeDisplay';
import { groupFieldsByTask } from '../components/FormViews/@components/TaskGrouper';
import {
  buildMechanicEffortSummaries,
  buildMechanicSubmissionLabourLineKey,
  groupMechanicFieldsByWorkIndex,
  isMechanicDailyLog,
  resolveMechanicLabourHours,
  sortMechanicGroupKeys
} from './mechanicDailyLog';
import { sumPersistedAllocationHoursByLineKey } from '../../../utils/labourOvertimeDisplay';

// Helper function to check if a task requires CHRIS reporting
// Sign activities start with 501, Culvert installation activities start with 20230, Cattle Guard is 109300
export const requiresCHRISReporting = (activityNumber: string | undefined): boolean => {
  if (!activityNumber) return false;
  // Sign activities start with 501
  if (activityNumber.startsWith('501')) return true;
  // Culvert installation activities start with 20230 (e.g., 202302, 202303, 202304, 202305)
  if (activityNumber.startsWith('20230')) return true;
  // Cattle Guard installation is activity 109300
  if (activityNumber === '109300') return true;
  return false;
};

export interface FormError {
  type: 'error';
  /** Stable machine-readable id for guards (e.g. approve blocking). */
  code?: string;
  taskGroupId: string;
  taskId?: number;
  taskName?: string;
  taskNumber?: string;
  message: string;
}

/** Persisted DRM labour allocations exist for line but Task Duration (Hours) is zero. */
export const TASK_DURATION_VS_ALLOCATION_CODE = 'TASK_DURATION_VS_ALLOCATION';

/** A CHRIS-reportable task has no CHRIS section; shown once per task (V2-051). */
export const CHRIS_SECTION_MISSING_CODE = 'CHRIS_SECTION_MISSING';

/** Work Completed line has a task selected but Task Duration (Hours) is zero. */
export const TASK_ZERO_LABOUR_HOURS_CODE = 'TASK_ZERO_LABOUR_HOURS';

const LUNCH_ACTIVITY_NUMBER = '999001';

export interface FormWarning {
  type: 'warning';
  /** e.g. TASK_ZERO_LABOUR_HOURS for summary banners and tab badges */
  code?: string;
  taskGroupId: string;
  taskId?: number;
  taskName?: string;
  taskNumber?: string;
  message: string;
  taskType?: string; // 'Sign installation', 'Culvert installation', 'Cattle Guard installation'
}

export interface FormValidationResult {
  errors: FormError[];
  warnings: FormWarning[];
  hasErrors: boolean;
  hasWarnings: boolean;
}

const safeParseFloat = (value: string | undefined, defaultValue: number = 0): number => {
  if (!value) return defaultValue;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? defaultValue : parsed;
};

/**
 * Computes total hours for a form (Labour + Vehicle + Hired Equipment HoursLabour + Meal).
 * Matches FormCard/FormSummaryView logic so Daily Log and Hired Equipment behave the same.
 */
export const getFormTotalHours = (form: Form): number => {
  if (isMechanicDailyLog(form)) {
    const efforts = buildMechanicEffortSummaries(form);
    const fromEfforts = efforts.reduce((s, e) => s + (e.excludeFromQuantityTotal ? 0 : e.quantity), 0);
    if (fromEfforts > 0) return fromEfforts;
    return safeParseFloat(form.fields.find((f) => f.field === 'TotalLabourTimeDec')?.value);
  }

  const taskGroups = form.fields.reduce((groups: { [key: string]: Form['fields'] }, field) => {
    if (!field.path.includes('WorkCompleted')) return groups;
    const taskMatch = field.path.match(/WorkCompleted\.Tasks (\d+\.\d+)/);
    if (!taskMatch) return groups;
    const taskGroupId = taskMatch[1];
    if (!groups[taskGroupId]) {
      groups[taskGroupId] = [];
    }
    groups[taskGroupId].push(field);
    return groups;
  }, {});

  let total = 0;

  for (const fields of Object.values(taskGroups)) {
    // Labour
    const labourHours = fields.find(f => f.field === 'TaskDurationHours' && f.path.includes('Timecard and Job C'))?.value || '0';
    total += safeParseFloat(labourHours);

    // Vehicle
    const primaryVehicleHours = fields.find(f => f.field === 'HoursVehicleUsage')?.value;
    const secondaryVehicleHours = fields.find(f => f.field === 'Hours Secondary Vehi')?.value;
    total += safeParseFloat(primaryVehicleHours);
    total += safeParseFloat(secondaryVehicleHours);

    // Hired Equipment: TimeWorkedJobCost 1.n -> HoursLabour per group
    const equipmentGroups: { [key: string]: Form['fields'] } = {};
    fields.forEach(field => {
      if (field.path.includes('TimeWorkedJobCost 1.')) {
        const equipmentMatch = field.path.match(/TimeWorkedJobCost 1\.(\d+)\./);
        if (equipmentMatch) {
          const equipmentGroupId = equipmentMatch[1];
          if (!equipmentGroups[equipmentGroupId]) {
            equipmentGroups[equipmentGroupId] = [];
          }
          equipmentGroups[equipmentGroupId].push(field);
        }
      }
    });
    Object.values(equipmentGroups).forEach(equipmentFields => {
      const hoursLabour = equipmentFields.find(f => f.field === 'HoursLabour')?.value || '0';
      total += safeParseFloat(hoursLabour);
    });

    // Meal
    const mealDurationMinutes = fields.find(f => f.field === 'MealDurationMinutes')?.value;
    if (mealDurationMinutes && safeParseFloat(mealDurationMinutes) > 0) {
      total += safeParseFloat(mealDurationMinutes) / 60;
    }
  }

  return total;
};

/**
 * Computes "display hours" for the form (Labour only - matches FormCard header "Hours").
 * Used for the 0-hours warning so the warning shows when the card would show 0 hours.
 * Does not include vehicle, equipment, or meal.
 */
export function getFormDisplayLabourHours(form: Form): number {
  if (isMechanicDailyLog(form)) {
    const efforts = buildMechanicEffortSummaries(form);
    const labour = efforts
      .filter((e) => e.type === 'Labour')
      .reduce((s, e) => s + e.quantity, 0);
    if (labour > 0) return labour;
    return safeParseFloat(form.fields.find((f) => f.field === 'TotalLabourTimeDec')?.value);
  }

  const taskGroups = form.fields.reduce((groups: { [key: string]: Form['fields'] }, field) => {
    if (!field.path.includes('WorkCompleted')) return groups;
    const taskMatch = field.path.match(/WorkCompleted\.Tasks (\d+\.\d+)/);
    if (!taskMatch) return groups;
    const taskGroupId = taskMatch[1];
    if (!groups[taskGroupId]) {
      groups[taskGroupId] = [];
    }
    groups[taskGroupId].push(field);
    return groups;
  }, {});

  let total = 0;
  for (const fields of Object.values(taskGroups)) {
    const labourHours = fields.find(f => f.field === 'TaskDurationHours' && f.path.includes('Timecard and Job C'))?.value || '0';
    total += safeParseFloat(labourHours);
  }
  return total;
}

/**
 * Returns true if the form is Hired Equipment or Subcontractor (no labour hours; same detection as FormCard).
 */
function isHiredEquipmentOrSubcontractorForm(form: Form): boolean {
  return form.fields.some(
    field =>
      field.path.includes('EquipmentTimeWorkedJ') ||
      field.field === 'AmountBilled' ||
      field.path.includes('SubcontractorNameCom')
  );
}

/**
 * Validates a form and collects all errors and warnings
 */
export const validateForm = (
  form: Form,
  availableTasks: Task[],
  hasBeenWithdrawn: boolean = false,
  labourOvertimeAllocations?: FormLabourOvertimeAllocationRow[]
): FormValidationResult => {
  const errors: FormError[] = [];
  const warnings: FormWarning[] = [];
  const mechanicDailyLog = isMechanicDailyLog(form);

  // Group fields by task
  const taskGroups = groupFieldsByTask(form.fields);

  // Check if form is pending or revisions completed (only check ineligible tasks for these)
  const isPendingOrRevisions = form.status === 'pending' || form.status === 'revisions completed';

  // Process each task group
  for (const [taskGroupId, fields] of Object.entries(taskGroups)) {
    // Get task ID from TaskId or Task field
    let taskId: number | null = null;
    const taskIdField = fields.find(f => f.field === 'TaskId' || f.path.match(/\.TaskId$/));
    if (taskIdField) {
      taskId = parseInt(taskIdField.value);
    } else {
      const taskField = fields.find(f => f.field === 'Task' && !f.path.includes('TaskId'));
      if (taskField && !isNaN(parseInt(taskField.value))) {
        taskId = parseInt(taskField.value);
      }
    }

    // Get task information
    const task = taskId ? availableTasks.find(t => t.id === taskId) : null;
    const taskName = task?.name || `Task ${taskId || taskGroupId}`;
    const taskNumber = task?.taskNumber || task?.id?.toString() || '';

    // Check for activity 999300 (Miscellaneous Quantified Task) - ERROR
    let hasActivity999300 = false;
    if (taskId) {
      hasActivity999300 = task?.activities?.some(activity => activity.activityNumber === '999300') || false;
    }
    // Fallback: check form fields
    if (!hasActivity999300) {
      hasActivity999300 = fields.some(f => 
        (f.field === 'ActivityNumber' || f.question === 'Activity Number') && 
        f.value === '999300'
      );
    }

    if (hasActivity999300) {
      errors.push({
        type: 'error',
        taskGroupId,
        taskId: taskId || undefined,
        taskName,
        taskNumber,
        message: `Task ${taskNumber ? `#${taskNumber}` : taskId || taskGroupId} has activity "Miscellaneous Quantified Task (#999300)" and must be updated to a specific Quantified Task before approval.`
      });
    }

    // Check for ineligible task statuses - ERROR
    if (isPendingOrRevisions && !hasBeenWithdrawn) {
      const ineligibleStatuses = ['PROPOSED', 'CLOSED', 'FINALIZED', 'CANCELLED'];
      if (task && task.status && ineligibleStatuses.includes(task.status.toUpperCase())) {
        const taskStatusDisplay = task.status.charAt(0).toUpperCase() + task.status.slice(1).toLowerCase();
        errors.push({
          type: 'error',
          taskGroupId,
          taskId: taskId || undefined,
          taskName,
          taskNumber,
          message: `Task ${taskNumber ? `#${taskNumber}` : taskId || taskGroupId} has status "${taskStatusDisplay}" and cannot receive effort. Please reassign the task before approval.`
        });
      }
    }

    // Check for missing CHRIS details - WARNING
    // Get activity number from task or form fields
    let activityNumber: string | undefined = undefined;
    if (taskId && task) {
      if (task.activities && task.activities.length > 0) {
        activityNumber = task.activities[0].activityNumber;
      }
    }
    // Fallback: check form fields
    if (!activityNumber) {
      const activityField = fields.find(f => 
        (f.field === 'ActivityNumber' || f.question === 'Activity Number') && f.value
      );
      if (activityField) {
        activityNumber = activityField.value;
      }
    }

    // Check if task requires CHRIS reporting
    const requiresCHRIS = activityNumber && requiresCHRISReporting(activityNumber);
    
    // Skip CHRIS warning for routine sign activities, but keep it for quantified sign activities
    // Routine tasks have task numbers starting with 'R' (e.g., 'R501200')
    // Quantified tasks have task numbers starting with 'Q' (e.g., 'Q501302')
    const isRoutineTask = taskNumber && taskNumber.startsWith('R');
    const isRoutineSignActivity = isRoutineTask && activityNumber && activityNumber.startsWith('501');
    
    if (requiresCHRIS && !isRoutineSignActivity && !mechanicDailyLog) {
      // Check all form fields for this task group to see if CHRIS section exists
      const taskGroupAllFields = form.fields.filter(f => 
        f.path.includes(`WorkCompleted.Tasks ${taskGroupId}`)
      );
      
      // Check if CHRIS section exists by looking for fields with CHRIS section in their path
      const hasCHRISSection = taskGroupAllFields.some(f => {
        const path = f.path;
        const pathSegments = path.split('.');
        return pathSegments.some(segment => {
          const segmentLower = segment.toLowerCase();
          return (
            segmentLower.includes('chrisreportingproper') ||
            (segmentLower.includes('chrisreportingprop') && !segmentLower.includes('ischrisreport'))
          );
        });
      });
      
      // If CHRIS section is absent, add warning
      if (!hasCHRISSection) {
        // Determine task type for message
        let taskType = 'installation';
        if (activityNumber && activityNumber.startsWith('501')) {
          taskType = 'Sign installation';
        } else if (activityNumber && activityNumber.startsWith('20230')) {
          taskType = 'Culvert installation';
        } else if (activityNumber === '109300') {
          taskType = 'Cattle Guard installation';
        }

        warnings.push({
          type: 'warning',
          code: CHRIS_SECTION_MISSING_CODE,
          taskGroupId,
          taskId: taskId || undefined,
          taskName,
          taskNumber,
          taskType,
          message: `${taskType} (Task ${taskNumber ? `#${taskNumber}` : taskId || taskGroupId}) submission does not have a corresponding CHRIS section completed.`
        });
      }
    }

    // RB: task selected but 0 labour hours on timecard — per-task warning (detailed tabs + banner)
    const ZERO_LABOUR_EPS = 1e-4;
    if (
      isPendingOrRevisions &&
      !mechanicDailyLog &&
      !isHiredEquipmentOrSubcontractorForm(form) &&
      taskId != null &&
      !Number.isNaN(taskId) &&
      taskId > 0 &&
      taskId !== 999001
    ) {
      const isLunchMealActivity = fields.some(
        (f) =>
          (f.field === 'ActivityNumber' || f.question === 'Activity Number') &&
          String(f.value ?? '').trim() === LUNCH_ACTIVITY_NUMBER
      );
      const timecardField = fields.find(
        (f) => f.field === 'TaskDurationHours' && f.path.includes('Timecard and Job C')
      );
      if (timecardField && !isLunchMealActivity) {
        const rawDur = timecardField.value;
        const duration = safeParseFloat(typeof rawDur === 'string' ? rawDur : String(rawDur ?? '0'));
        if (duration <= ZERO_LABOUR_EPS) {
          warnings.push({
            type: 'warning',
            code: TASK_ZERO_LABOUR_HOURS_CODE,
            taskGroupId,
            taskId: taskId ?? undefined,
            taskName,
            taskNumber,
            message: `Task ${taskNumber ? `#${taskNumber}` : taskId} (${taskName}) has 0 labour hours. Enter Task Duration (Hours) if work was performed on this task.`,
          });
        }
      }
    }

    // RB daily logs: Task Duration 0 vs persisted DRM allocation hours for same Work Completed line
    const TASK_DURATION_VS_ALLOC_EPS = 1e-4;
    if (
      !mechanicDailyLog &&
      !isHiredEquipmentOrSubcontractorForm(form) &&
      labourOvertimeAllocations &&
      labourOvertimeAllocations.length > 0 &&
      taskId != null &&
      taskId > 0
    ) {
      const timecardDur = fields.find(
        (f) => f.field === 'TaskDurationHours' && f.path.includes('Timecard and Job C')
      )?.value;
      const duration = safeParseFloat(
        typeof timecardDur === 'string' ? timecardDur : String(timecardDur ?? '0')
      );
      const lineKey = `WorkCompleted.Tasks ${taskGroupId}|task=${taskId}`;
      const allocSum = sumPersistedAllocationHoursForWorkCompleted(
        taskId,
        lineKey,
        labourOvertimeAllocations
      );
      if (duration <= TASK_DURATION_VS_ALLOC_EPS && allocSum > TASK_DURATION_VS_ALLOC_EPS) {
        errors.push({
          type: 'error',
          code: TASK_DURATION_VS_ALLOCATION_CODE,
          taskGroupId,
          taskId: taskId ?? undefined,
          taskName,
          taskNumber,
          message: `Task Duration (Hours) is 0 but persisted labour overtime allocations show ${allocSum.toFixed(2)}h for this Work Completed line (${taskName}). Correct Task Duration or clear/adjust allocations before approval.`,
        });
      }
    }
  }

  // Mechanic daily logs: per-group labour hours vs DRM allocations (same rules as RB, mechanic hour resolver)
  if (
    mechanicDailyLog &&
    isPendingOrRevisions &&
    !isHiredEquipmentOrSubcontractorForm(form)
  ) {
    const ZERO_LABOUR_EPS = 1e-4;
    const TASK_DURATION_VS_ALLOC_EPS = 1e-4;
    const groups = groupMechanicFieldsByWorkIndex(form);
    const sortedGroupIds = Object.keys(groups).sort(sortMechanicGroupKeys);

    for (const groupId of sortedGroupIds) {
      const groupFields = groups[groupId];
      const preferActivityDuration = groupId.includes('::');
      const duration = resolveMechanicLabourHours(groupFields, { preferActivityDuration });
      const activityNumber = groupFields.find(
        (f) =>
          (f.field === 'ActivityNumber' || f.field === 'Activity') &&
          (f.path.includes('MechanicTasks') || f.path.includes('.Tasks.Activity'))
      )?.value;
      const isLunchMealActivity = String(activityNumber ?? '').trim() === LUNCH_ACTIVITY_NUMBER;
      const hasMechanicActivityFields = groupFields.some(
        (f) =>
          f.path.includes('MechanicTasks') &&
          (f.field === 'TotalTime' ||
            f.field === 'TotalTimeSpent' ||
            f.field === 'ActivityNumber' ||
            f.field === 'Activity')
      );

      if (hasMechanicActivityFields && !isLunchMealActivity && duration <= ZERO_LABOUR_EPS) {
        warnings.push({
          type: 'warning',
          code: TASK_ZERO_LABOUR_HOURS_CODE,
          taskGroupId: groupId,
          message: `Activity on work block ${groupId} has 0 labour hours. Enter duration if work was performed.`,
        });
      }

      if (labourOvertimeAllocations && labourOvertimeAllocations.length > 0) {
        const lineKey = buildMechanicSubmissionLabourLineKey(groupId);
        const allocSum = sumPersistedAllocationHoursByLineKey(lineKey, labourOvertimeAllocations);
        if (duration <= TASK_DURATION_VS_ALLOC_EPS && allocSum > TASK_DURATION_VS_ALLOC_EPS) {
          errors.push({
            type: 'error',
            code: TASK_DURATION_VS_ALLOCATION_CODE,
            taskGroupId: groupId,
            message: `Labour hours are 0 but persisted labour overtime allocations show ${allocSum.toFixed(2)}h for this activity (${groupId}). Correct duration or clear/adjust allocations before approval.`,
          });
        }
      }
    }
  }

  // Form-level: 0 hours warning only for unapproved forms; skip Hired Equipment and Subcontractor (no labour hours)
  if (
    getFormDisplayLabourHours(form) === 0 &&
    isPendingOrRevisions &&
    !isHiredEquipmentOrSubcontractorForm(form)
  ) {
    warnings.push({
      type: 'warning',
      taskGroupId: 'form',
      message: 'This timecard has no hours entered. Please ensure time has been entered before approval.'
    });
  }

  const merged = mergeChrisWarnings(warnings);
  return {
    errors,
    warnings: merged,
    hasErrors: errors.length > 0,
    hasWarnings: merged.length > 0
  };
};

/**
 * V2-051: the CHRIS warning shows once per task with the number of lines, not once per line.
 * The first line's warning is kept in place; the rest of that task's are dropped.
 */
function mergeChrisWarnings(warnings: FormWarning[]): FormWarning[] {
  const keyOf = (w: FormWarning) => (w.taskId != null ? `id:${w.taskId}` : `group:${w.taskGroupId}`);
  const counts = new Map<string, number>();
  for (const w of warnings) {
    if (w.code === CHRIS_SECTION_MISSING_CODE) counts.set(keyOf(w), (counts.get(keyOf(w)) ?? 0) + 1);
  }
  const seen = new Set<string>();
  const out: FormWarning[] = [];
  for (const w of warnings) {
    if (w.code !== CHRIS_SECTION_MISSING_CODE) {
      out.push(w);
      continue;
    }
    const key = keyOf(w);
    if (seen.has(key)) continue;
    seen.add(key);
    const lines = counts.get(key) ?? 1;
    const ref = w.taskNumber ? `#${w.taskNumber}` : w.taskId ?? w.taskGroupId;
    out.push({
      ...w,
      message: `${w.taskType} (Task ${ref}) submission does not have a corresponding CHRIS section completed${
        lines > 1 ? ` – ${lines} lines` : ''
      }.`,
    });
  }
  return out;
}

